import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { ErrorMessage, Field, MessageWrapper } from '@atlaskit/form';
import { Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

// This file is based on confluence/next/packages/global-feedback-collector/src/EmojiRatingSelector.tsx

const styles = cssMap({
	emojiSelectionContainer: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: token('space.100'),
		marginBottom: token('space.200'),
	},
	emojiButton: {
		font: token('font.heading.xlarge'),
		backgroundColor: 'transparent',

		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),

		borderColor: 'transparent',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	emojiButtonSelected: {
		backgroundColor: token('color.background.selected'),

		borderColor: token('color.border.focused'),

		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
});

export enum ExperienceRating {
	VeryDissatisfied = 'Very dissatisfied',
	Dissatisfied = 'Dissatisfied',
	Neutral = 'Neutral',
	Satisfied = 'Satisfied',
	VerySatisfied = 'Very satisfied',
}

export const ExperienceRatingToNumber: {
	'Very dissatisfied': number;
	Dissatisfied: number;
	Neutral: number;
	Satisfied: number;
	'Very satisfied': number;
} = {
	[ExperienceRating.VeryDissatisfied]: 1,
	[ExperienceRating.Dissatisfied]: 2,
	[ExperienceRating.Neutral]: 3,
	[ExperienceRating.Satisfied]: 4,
	[ExperienceRating.VerySatisfied]: 5,
};

const OPTION_DATA = [
	{ emoji: '😠', experienceRating: ExperienceRating.VeryDissatisfied },
	{ emoji: '☹️', experienceRating: ExperienceRating.Dissatisfied },
	{ emoji: '😐', experienceRating: ExperienceRating.Neutral },
	{ emoji: '🙂', experienceRating: ExperienceRating.Satisfied },
	{ emoji: '😃', experienceRating: ExperienceRating.VerySatisfied },
];

export type EmojiRatingSelectorProps = {
	rating: ExperienceRating | undefined;
	setRating: React.Dispatch<React.SetStateAction<ExperienceRating | undefined>>;
	label: string;
};

export const EmojiRatingSelector = ({
	rating,
	setRating,
	label,
}: EmojiRatingSelectorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const onEmojiButtonClick = useCallback(
		(
			option: { experienceRating: ExperienceRating },
			fieldProps: { onChange: (value: ExperienceRating) => void },
		) =>
			(e: React.MouseEvent<HTMLButtonElement>) => {
				e.preventDefault();
				setRating(option.experienceRating);
				fieldProps.onChange(option.experienceRating);
			},
		[setRating],
	);

	const validateRating = (value: ExperienceRating | undefined) => {
		if (!value) {
			return formatMessage(messages.validationErrorRatingRequired);
		}
		return undefined;
	};

	return (
		<Field
			label={label}
			isRequired={true}
			name="rate-experience"
			id="rate-experience"
			validate={validateRating}
		>
			{({ fieldProps, error }) => (
				<>
					<Flex data-name={fieldProps.id} xcss={styles.emojiSelectionContainer} role="radiogroup">
						{OPTION_DATA.map((option) => (
							<Pressable
								role="radio"
								aria-checked={rating === option.experienceRating}
								key={option.experienceRating}
								data-name={option.experienceRating}
								data-value={option.experienceRating}
								testId={`emoji-rating-${ExperienceRatingToNumber[option.experienceRating]}`}
								onClick={onEmojiButtonClick(option, fieldProps)}
								xcss={cx(
									styles.emojiButton,
									rating === option.experienceRating && styles.emojiButtonSelected,
								)}
							>
								{option.emoji}
							</Pressable>
						))}
					</Flex>
					<MessageWrapper>{error && <ErrorMessage>{error}</ErrorMessage>}</MessageWrapper>
				</>
			)}
		</Field>
	);
};
